import React, { useState, useEffect } from 'react';
import { bulkImportAPI, projectsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Upload, FileSpreadsheet, Download, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const ENTITY_CONFIGS = {
  clients:  { label: 'Clients',   color: 'text-violet-600',  api: bulkImportAPI.importClients, templateApi: bulkImportAPI.templateClients, needsProject: false },
  vendors:  { label: 'Vendors',   color: 'text-amber-600',   api: bulkImportAPI.importVendors, templateApi: bulkImportAPI.templateVendors, needsProject: false },
  ffe:      { label: 'FF&E',      color: 'text-sky-600',     api: bulkImportAPI.importFFE,     templateApi: bulkImportAPI.templateFFE,     needsProject: 'optional' },
  tasks:    { label: 'Tasks',     color: 'text-emerald-600', api: bulkImportAPI.importTasks,   templateApi: bulkImportAPI.templateTasks,   needsProject: true },
};

function EntityImport({ entity }) {
  const cfg = ENTITY_CONFIGS[entity];
  const [csvData, setCsvData] = useState('');
  const [projectId, setProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  const [defaultMarkup, setDefaultMarkup] = useState(30);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    cfg.templateApi().then(r => setTemplate(r.data)).catch(() => {});
    if (cfg.needsProject) {
      projectsAPI.list().then(r => setProjects(r.data)).catch(() => {});
    }
  }, [entity]); // eslint-disable-line

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvData(ev.target.result);
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    if (!template) return;
    const csv = `${template.headers}\n${template.example}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${entity}_template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!csvData.trim()) { toast.error('Upload a CSV or paste data'); return; }
    if (cfg.needsProject === true && !projectId) { toast.error('Select a project'); return; }
    setLoading(true); setResult(null);
    try {
      const payload = { csv_data: csvData };
      if (projectId) payload.project_id = projectId;
      if (entity === 'ffe') payload.default_markup = defaultMarkup;
      const res = await cfg.api(payload);
      setResult(res.data);
      if (res.data.imported > 0) toast.success(`Imported ${res.data.imported} ${cfg.label.toLowerCase()}`);
      if (res.data.errors?.length) toast.warning(`${res.data.errors.length} rows had errors`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      {template && (
        <Card className="bg-muted/30">
          <CardContent className="p-4 flex items-start gap-3">
            <FileSpreadsheet className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium mb-1">CSV columns</div>
              <div className="text-xs text-muted-foreground break-all font-mono">{template.headers}</div>
              <div className="text-xs text-muted-foreground mt-1"><span className="font-medium">Example:</span> {template.example}</div>
            </div>
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid={`download-template-${entity}`}><Download className="h-3 w-3 mr-1" />Template</Button>
          </CardContent>
        </Card>
      )}

      {cfg.needsProject && (
        <div>
          <Label>Project {cfg.needsProject === true && '*'}</Label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger data-testid={`project-select-${entity}`}><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      {entity === 'ffe' && (
        <div><Label>Default Markup % (for rows without a value)</Label><Input type="number" value={defaultMarkup} onChange={e => setDefaultMarkup(e.target.value)} /></div>
      )}

      <div>
        <Label>Upload CSV file</Label>
        <Input type="file" accept=".csv,text/csv" onChange={handleFileUpload} data-testid={`file-upload-${entity}`} />
      </div>

      <div>
        <Label>...or paste CSV data</Label>
        <Textarea rows={8} placeholder={template?.headers} value={csvData} onChange={e => setCsvData(e.target.value)} className="font-mono text-xs" data-testid={`csv-textarea-${entity}`} />
      </div>

      <Button onClick={handleImport} disabled={loading || !csvData.trim()} data-testid={`import-btn-${entity}`}>
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
        Import {cfg.label}
      </Button>

      {result && (
        <Card className={result.errors?.length ? 'border-amber-200' : 'border-emerald-200'}>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2">
              {result.errors?.length ? <AlertTriangle className="h-5 w-5 text-amber-600" /> : <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
              <span className="font-medium">Imported {result.imported} / {result.total_rows} rows</span>
            </div>
            {result.errors?.length > 0 && (
              <div>
                <div className="text-sm font-medium text-amber-700">Errors:</div>
                <div className="text-xs max-h-40 overflow-y-auto space-y-1 mt-1" data-testid={`errors-${entity}`}>
                  {result.errors.map((e, i) => <div key={i} className="text-amber-700">• {e}</div>)}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function BulkImport() {
  return (
    <Layout>
      <div className="space-y-6" data-testid="bulk-import-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-light tracking-tight flex items-center gap-3">
            <Upload className="h-7 w-7 text-sky-500" />Bulk CSV Import
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Migrate data from spreadsheets with a single upload</p>
        </div>

        <Card>
          <CardContent className="p-6">
            <Tabs defaultValue="clients">
              <TabsList className="grid grid-cols-4 mb-6">
                {Object.entries(ENTITY_CONFIGS).map(([key, c]) => (
                  <TabsTrigger key={key} value={key} data-testid={`tab-${key}`}>{c.label}</TabsTrigger>
                ))}
              </TabsList>
              {Object.keys(ENTITY_CONFIGS).map(key => (
                <TabsContent key={key} value={key}>
                  <EntityImport entity={key} />
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
